/**
 * Vendor entity adapter (P15.1).
 *
 * Wires `AdminListView` + `AdminEntityDrawer` for the simplest
 * Foundation entity -- no parent FK, no link tables. Acts as the
 * reference implementation that P15.2-P15.3 mirror for the other
 * six entity types.
 */
import { i18n } from "shared/i18n";
import { makeTwinDrillSection } from "./TwinDrillSection";
import { VendorPassportSection } from "foundation/admin/vendorPassportSection";
import type { AdminColumn, EntityAdapter, FormField } from "./types";

export interface Vendor {
  id: string;
  code: string;
  name: string;
  description: string | null;
  material_flag: boolean;
  // P136 (P117f.4): implied-SSOT vendor-profile fields.
  country: string | null;
  criticality_tier: string | null;
  contract_value_sar: string | null;
  exit_plan_ref: string | null;
  /** Round 20 W6 -- commercial registration (CR) number. */
  registration_number?: string | null;
  /** P440 -- cost to move one MVB service off this vendor. */
  substitution_cost_sar?: string | null;
  /** Round 20 V7. */
  contracts_unsupplied?: string[];
  created_at: string;
  updated_at: string;
}

export interface VendorIn {
  code: string;
  name: string;
  description: string | null;
  material_flag: boolean;
  country?: string | null;
  criticality_tier?: string | null;
  contract_value_sar?: string | number | null;
  exit_plan_ref?: string | null;
  registration_number?: string | null;
  substitution_cost_sar?: string | number | null;
}

export type VendorPatch = Partial<VendorIn>;

const columns: AdminColumn<Vendor>[] = [
  {
    key: "code",
    label: "Code",
    sortValue: (r) => r.code,
    render: (r) => <span className="font-mono text-xs text-slate-700">{r.code}</span>,
  },
  {
    key: "name",
    label: "Name",
    sortValue: (r) => r.name,
    render: (r) => (
      <span className="font-medium text-slate-900">
        {r.name}
        {/* Round 20 V7 -- a contract naming a service this supplier is
            not linked to. */}
        {(r.contracts_unsupplied ?? []).length > 0 && (
          <span
            data-testid="vendor-register-unsupplied"
            className="ms-2 block text-[11px] font-normal text-rose-700"
          >
            {i18n.t("supplierCoherence.vendorRow", {
              list: (r.contracts_unsupplied ?? []).join("; "),
            })}
          </span>
        )}
      </span>
    ),
  },
  {
    key: "material",
    label: "Material",
    sortValue: (r) => (r.material_flag ? 1 : 0),
    render: (r) =>
      r.material_flag ? (
        <span
          data-testid="vendor-material-chip"
          className="inline-flex items-center rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 text-xs font-medium"
        >
          {i18n.t("screens.vendorAdapter.material")}
        </span>
      ) : (
        <span className="text-xs text-slate-400">--</span>
      ),
  },
  {
    key: "description",
    label: "Description",
    render: (r) => (
      <span className="text-xs text-slate-600 line-clamp-1">{r.description ?? ""}</span>
    ),
  },
];

const formFields: FormField<VendorIn>[] = [
  { kind: "text", key: "code", label: "Code", required: true, maxLength: 64 },
  { kind: "text", key: "name", label: "Name", required: true, maxLength: 255 },
  { kind: "textarea", key: "description", label: "Description", rows: 3 },
  { kind: "bool", key: "material_flag", label: "Material third-party" },
  // Implied-SSOT vendor profile (P136 / P117f.4).
  {
    kind: "text",
    key: "country",
    label: "Country",
    maxLength: 64,
    group: "Vendor profile",
  },
  {
    kind: "text",
    key: "criticality_tier",
    label: "Criticality tier",
    maxLength: 32,
    group: "Vendor profile",
  },
  {
    kind: "decimal",
    key: "contract_value_sar",
    label: "Contract value",
    currencyHint: "SAR",
    group: "Vendor profile",
  },
  {
    kind: "text",
    key: "exit_plan_ref",
    label: "Exit plan ref",
    maxLength: 255,
    group: "Vendor profile",
  },
  {
    kind: "text",
    key: "registration_number",
    label: "Registration (CR) number",
    maxLength: 64,
    group: "Vendor profile",
  },
  {
    kind: "decimal",
    key: "substitution_cost_sar",
    label: "Cost to move one MVB service off this vendor",
    currencyHint: "SAR",
    group: "Cost basis",
  },
];

export const vendorAdapter: EntityAdapter<Vendor, VendorIn, VendorPatch> = {
  i18nPrefix: "modules.foundation.admin.vendors",
  importKind: "vendor",
  endpoint: "/v1/admin/foundation/vendors",
  queryKey: ["foundation-admin", "vendors"] as const,
  columns,
  defaultSort: { key: "code", dir: "asc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) => `${r.code} ${r.name} ${r.description ?? ""}`,
  formFields,
  formInitial: () => ({
    code: "",
    name: "",
    description: null,
    material_flag: false,
    country: null,
    criticality_tier: null,
    contract_value_sar: null,
    exit_plan_ref: null,
    registration_number: null,
    substitution_cost_sar: null,
  }),
  rowToPatch: (r) => ({
    code: r.code,
    name: r.name,
    description: r.description,
    material_flag: r.material_flag,
    country: r.country,
    criticality_tier: r.criticality_tier,
    contract_value_sar: r.contract_value_sar,
    exit_plan_ref: r.exit_plan_ref,
    registration_number: r.registration_number ?? null,
    substitution_cost_sar: r.substitution_cost_sar ?? null,
  }),
  extraSections: [
    makeTwinDrillSection("vendor", "foundation-vendor-twin-drill"),
    VendorPassportSection,
  ],
};
